/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import {useAuth} from '../../../modules/auth'
import {useLocation} from 'react-router-dom'
import db from '../../../../services/firebase'
import {Helmet} from 'react-helmet'
import {collection, deleteDoc, doc, onSnapshot, updateDoc} from 'firebase/firestore'
import PolicyDrawer from '../components/PolicyDrawer'
import AssignOwner from '../components/AssignOwner'
import useCrispChat from 'app/Hooks/useCrispChat'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import {PolicyRow} from '../components/table-components/PolicyRow'
import EmptyState from 'app/components/EmptyState'
import CardHeader from '../components/table-components/CardHeader'
import { FileItemType } from '../Utils/PolicyUtils'
import useAuditContext from 'app/Hooks/useAuditContext'
import Button from 'app/components/Button'

type Props = {
  className: string
  getPolicyID?: any
  tabIndex?: any
}

const PolicyLibraryTable: React.FC<Props> = ({getPolicyID}) => {
  const [policy, setPolicy] = useState<FileItemType[]>([])
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const location = useLocation()
  const {currentUser} = useAuth()
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isModalOpen2, setIsModalOpen2] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredPolicies, setFilteredPolicies] = useState<FileItemType[]>([])
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [policyId, setPolicyId] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([])
  const searchParams = new URLSearchParams(location.search)
  const policy_id = searchParams.get('policies')
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const {toggleCrispChat} = useCrispChat()
  const [currentPlan, setPlan] = useState<any[]>([])
  const isTrialPlan = currentPlan[0]?.account_type === 'Trial' || currentPlan[0]?.account_type === 'Trial-plus'
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedPolicyToDelete, setSelectedPolicyToDelete] = useState<FileItemType | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [audits, setAudits] = useState<any[]>([])
  const { currentAuditContext } = useAuditContext()

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'audits')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      let Data = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          status: doc.data()?.status,
          createdAt: doc.data().createdAt,
          auditor: doc.data().auditor,
          framework: doc.data().framework,
        }))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .filter((audit) => audit.status !== 'Completed')

      if (currentUser?.role === 'Auditor') {
        Data = Data.filter((audit) => audit.auditor?.id === currentUser?.uid)
      }
      setAudits(Data)
    })

    return unsubscribe
  }, [currentUser?.tenantId, currentUser?.role, currentUser?.uid])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 2000)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    getPolicy()
  }, [activeFrameworks])

  useEffect(() => {
    getActiveFramworks()
  }, [])

  const getActiveFramworks = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) return
      const tenantRef = doc(db, 'tenants', tenantId)
      const testsCollectionRef = collection(tenantRef, 'active_frameworks')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }))
        const frameworks = frameworksData[0]?.frameworks || []
        setActiveFrameworks(frameworks)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting tests:', error)
    }
  }

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) return
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const risksData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date,
          plan: doc.data().plan,
          uid: doc.data().uid,
        }))
        .filter((data) => !data.end_date)

      const filter = risksData.filter((f) => f.uid === currentUser?.uid)
      setPlan(filter)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    if (policy_id) {
      setIsModalOpen(true)
      setPolicyId(policy_id || '')
    }
  }, [policy_id])

  const getPolicy = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) return
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'policies')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        let policyData = snapshot.docs
          .map((doc) => ({
            ...(doc.data() as FileItemType),
            id: doc.id,
            frameworks: doc.data().frameworks,
          }))
          .filter((policy) => policy.status !== 'Deactivated')
          .sort((a, b) => {
            const policyA = a.policy || ''
            const policyB = b.policy || ''
            return policyA.localeCompare(policyB)
          })

        let frameworksToFilter = activeFrameworks
        if (currentUser?.role === 'Auditor' && audits.length > 0) {
          frameworksToFilter = Array.from(new Set(audits.flatMap(audit => audit.framework || [])))
        }

        if(currentAuditContext){
          frameworksToFilter = Array.from(new Set(currentAuditContext.framework || []))
        }

        policyData = policyData
          .filter((policy) => {
            if (policy.author === 'Custom policy') return true;
            if (!policy.frameworks || !Array.isArray(policy.frameworks)) return false;
            return policy.frameworks.some((policyFramework) =>
              frameworksToFilter.includes(policyFramework)
            );
          })
          .map((policy) => ({
            ...policy,
            frameworks: policy.frameworks?.filter((framework) =>
              frameworksToFilter.includes(framework)
            ) || [],
          }));

        if (currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') {
          policyData = policyData.map((policy, index) => {
            if (index >= 5) {
              return {
                ...policy,
                policy: maskStringPreservingSpaces(policy.policy),
                owner: '',
                last_approved: '',
                review_date: '',
              }
            }
            return policy
          })
        }
        setPolicy(policyData)
        setFilteredPolicies(policyData)
        setShowEmptyState(false)
      })

      return unsubscribe
    } catch (error) {
      setShowEmptyState(false)
    }
  }

  const maskStringPreservingSpaces = (
    str: string,
    visibleStartLength = 5,
    visibleEndLength = 4
  ) => {
    if (!str || str.length <= visibleStartLength + visibleEndLength) return '****'
    const start = str.slice(0, visibleStartLength)
    const end = str.slice(-visibleEndLength)
    const middle = str.slice(visibleStartLength, -visibleEndLength)
    const maskedMiddle = middle
      .split('')
      .map((char) => (char === ' ' ? ' ' : '*'))
      .join('')
    return `${start}${maskedMiddle}${end}`
  }

  const handleDownloadClick = async (policyData: FileItemType, isTemplate: boolean) => {
    try {
      const fileUrl = isTemplate ? policyData.fileUrl : policyData?.customfile?.[0]?.url
      const response = await fetch(fileUrl)
      const fileContent = await response.blob()
      const blob = new Blob([fileContent], {type: 'application/octet-stream'})
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = isTemplate ? `${policyData.policy}.docx` : `${policyData.customfile?.[0]?.name}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Error downloading policy file:', error)
    }
  }

  const handleCheckboxChange = async (id: string) => {
    try {
      const updatedMainPolicy = policy.map((item) => ({
        ...item,
        isChecked: item.id === id ? !item.isChecked : item.isChecked,
      }))
      setPolicy(updatedMainPolicy)
      const updatedFiltered = updatedMainPolicy.filter(
        (item) =>
          !selectedStatus ||
          (item.status && item.status.toLowerCase() === selectedStatus.toLowerCase())
      )
      setFilteredPolicies(updatedFiltered)
      const newSelectedItems = updatedMainPolicy
        .filter((item) => item.isChecked)
        .map((item) => item.id)
      setSelectedPolicyIds(newSelectedItems)
      setSelectedItems(newSelectedItems)
      setSelectAllChecked(updatedFiltered.every((item) => item.isChecked))
      setIsChecked(newSelectedItems.length > 0)
    } catch (error) {
      console.error('Error updating database:', error)
    }
  }

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked
    setSelectAllChecked(isChecked)
    const updatedMainPolicy = policy.map((item) => ({
      ...item,
      isChecked: isChecked,
    }))
    setPolicy(updatedMainPolicy)
    const updatedFiltered = updatedMainPolicy.filter(
      (item) =>
        !selectedStatus ||
        (item.status && item.status.toLowerCase() === selectedStatus.toLowerCase())
    )
    setFilteredPolicies(updatedFiltered)
    const newSelectedItems = isChecked ? updatedFiltered.map((item) => item.id) : []
    setSelectedPolicyIds(newSelectedItems)
    setSelectedItems(newSelectedItems)
    setIsChecked(isChecked)
  }

  const handleDeleteClick = (policyData: FileItemType) => {
    setSelectedPolicyToDelete(policyData)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!selectedPolicyToDelete) return
    setIsDeleting(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'policies')
      const docRef = doc(policyRef, selectedPolicyToDelete.id)
      await deleteDoc(docRef)
      const testsCollectionRef = collection(tenantRef, 'tests')
      const unsubscribe = onSnapshot(testsCollectionRef, async (snapshot) => {
        const testsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          policies: doc.data().policies,
        }))
        const filteredTests = testsData.filter(
          (test) => test?.policies === selectedPolicyToDelete.policy
        )
        if (filteredTests.length > 0) {
          for (const test of filteredTests) {
            const testDocRef = doc(tenantRef, 'tests', test.id)
            try {
              await updateDoc(testDocRef, {status: 'Fail'})
            } catch (error) {
              console.error(`Error updating test ${test.id}:`, error)
            }
          }
        }
      })
      getPolicy()
      setIsDeleteModalOpen(false)
      setSelectedPolicyToDelete(null)
      showSuccessToast({message: `Policy "${selectedPolicyToDelete.policy}" has been deleted.`})
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting the policy.'})
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeselectClick = async () => {
    try {
      setIsChecked(false)
      setSelectAllChecked(false)
      setSelectedItems([])
      const updatedItems = policy.map((item) => ({
        ...item,
        isChecked: false,
      }))
      setPolicy(updatedItems)
      setFilteredPolicies(updatedItems)
    } catch (error) {
      console.error('Error updating database after deselect:', error)
    }
  }

  const handleOpenModal = (id: string) => {
    setIsModalOpen(true)
    setPolicyId(id)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleCloseModal2 = () => {
    setIsModalOpen2(false)
  }

  const handleAssignOwnerClick = () => {
    const selectedPolicyIds = filteredPolicies
      .filter((item) => item.isChecked)
      .map((item) => item.id) as string[]
    setSelectedPolicyIds(selectedPolicyIds)
    setIsModalOpen2(true)
  }

  const handleApplyFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const status = e.target.value
    setSelectedStatus(status)
    if (status === '') {
      setSelectedStatus('')
      setFilteredPolicies(policy)
      setShowEmptyState(policy.length === 0)
      return
    }
    const filtered = policy.filter(
      (data) => data.status && data.status.toLowerCase() === status.toLowerCase()
    )
    setFilteredPolicies(filtered)
    setShowEmptyState(filtered.length === 0)
  }

  useEffect(() => {
    let filteredItems: FileItemType[] = policy;

    if (selectedStatus) {
      filteredItems = filteredItems.filter(
        (item) =>
          item.status &&
          item.status.toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    if (searchTerm) {
      if (isTrialPlan) {
        filteredItems = filteredItems
          .slice(0, 5)
          .filter((item) => item.policy.toLowerCase().includes(searchTerm));
      } else {
        filteredItems = filteredItems.filter((item) =>
          item.policy.toLowerCase().includes(searchTerm)
        );
      }
    } 

    setFilteredPolicies(filteredItems);
    setSearchLength(filteredItems.length === 0);
  }, [searchTerm, policy, isTrialPlan, selectedStatus]);

  return (
    <>
      <Helmet>
        <title>SecureSlate | Policy Library</title>
      </Helmet>
      <CardHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedStatus={selectedStatus}
        isChecked={isChecked}
        selectedItems={selectedItems}
        policies={policy}
        handleApplyFilter={handleApplyFilter}
        handleDeselectClick={handleDeselectClick}
        handleAssignOwnerClick={handleAssignOwnerClick}
        currentAuditContext={currentAuditContext}
        currentUser={currentUser}
      />
      <div className='card-body py-3'>
        <div className='table-responsive' style={{minHeight: '55vh', overflowY: 'auto'}}>
        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
            <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                <th className='w-0px pe-2'>
                  {!isTrialPlan && (
                    <div className='form-check form-check-sm form-check-custom form-check-solid me-3'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        data-kt-check='true'
                        data-kt-check-target='#kt_file_manager_list .form-check-input'
                        value='0'
                        id='select-all'
                        checked={selectAllChecked}
                        onChange={handleSelectAllChange}
                      />
                    </div>
                  )}
                </th>
                <th className='ps-4 min-w-300px rounded-start'>NAME</th>
                <th className='min-w-100px'>FRAMEWORKS</th>
                <th className='min-w-100px'>STATUS</th>
                <th className='min-w-100px'>LAST APPROVED</th>
                <th className='min-w-100px'>NEXT REVIEW</th>
                <th className='min-w-100px'>OWNER</th>
                <th className='min-w-20px text-end rounded-end'></th>
              </tr>
            </thead>
            <tbody>
              {isModalOpen2 && (
                <AssignOwner
                  isOpen={isModalOpen2}
                  onClose={handleCloseModal2}
                  ids={selectedPolicyIds}
                />
              )}
              <PolicyDrawer isOpen={isModalOpen} onClose={handleCloseModal} id={policyId} />
              {filteredPolicies && filteredPolicies.length > 0 ? (
                filteredPolicies.map((policyData, index) =>
                  index === 5 && isTrialPlan ? (
                    <tr key='message-row'>
                        <td colSpan={100}>
                          <div className='d-flex align-items-center justify-content-center gap-3 fw-semibold text-capitalize'>
                            <span>To unlock full access and view all data, please upgrade your plan.</span>
                            <Button btnType='primary' onClick={toggleCrispChat}>
                              Contact Sales
                            </Button>
                          </div>
                        </td>
                      </tr>
                  ) : (
                    <PolicyRow
                      key={policyData.id}
                      policyData={policyData}
                      index={index}
                      isTrialPlan={isTrialPlan}
                      handleCheckboxChange={handleCheckboxChange}
                      handleOpenModal={handleOpenModal}
                      handleDownloadClick={handleDownloadClick}
                      handleDeleteClick={handleDeleteClick}
                      currentAuditContext={currentAuditContext}
                    />
                  )
                )
              ) : (
                <tr>
                  <td colSpan={10} className='text-center'>
                    {searchLength ? (
                      <EmptyState
                        title='No polices Found'
                        description="We couldn't find any policies that match your search. Maybe try a different search!"
                      />
                    ) : (
                      <EmptyState
                        title='No polices Added'
                        description='Add the polices that all employees within your organization are required to adhere to.'
                        isLoading={!showEmptyState}
                      />
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        hideModal={() => {
          setIsDeleteModalOpen(false)
          setSelectedPolicyToDelete(null)
        }}
        confirmBtnClickHandler={handleConfirmDelete}
        toDeleteItemTitle={selectedPolicyToDelete?.policy}
        isLoading={isDeleting}
        confirmButtonText='Remove'
      />
    </>
  )
}

export {PolicyLibraryTable}
